import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { closeCreatePartnerDiscountModal, createDiscountSchema, fetchCreatePartnerDiscount, selectIsCreatePartnerDiscountModalOpen, selectOnCreate } from "../../model"
import { Modal } from "@/shared/ui/modal"
import { yupResolver } from "@hookform/resolvers/yup"
import { SubmitHandler, useForm } from "react-hook-form"
import { FormInput } from "@/shared/ui/form-input"
import { selectOEMGroups, selectPartners } from "@/entities/partner"
import { useMemo } from "react"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"

const accrualTypesSelectOptions = [
    {
        value: 1,
        label: '%'
    },
    {
        value: 2,
        label: 'руб'
    }
]

interface CreatePartnerDiscountFormValues {
    partner_id: string;
    accrual_type: number;
    value: number;
    started_date: Date;
    finished_date?: Date;
    priority: number;
    oem_groups: number[];
}

const initialState: CreatePartnerDiscountFormValues = {
    partner_id: "",
    accrual_type: 0,
    value: 0,
    priority: 0,
    oem_groups: [],
    started_date: undefined
}

export const CreatePartnerDiscountModal = () => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectIsCreatePartnerDiscountModalOpen)
    const partners = useAppSelector(selectPartners)
    const oemGroups = useAppSelector(selectOEMGroups)
    const onCreate = useAppSelector(selectOnCreate)

    const handleClose = () => {
        dispatch(closeCreatePartnerDiscountModal())
    }

    const partnerSelectOptions = useMemo(() => partners.map(partner => ({ value: partner.id, label: partner.name })), [partners])
    const oemgroupsSelectOptions = useMemo(() => oemGroups.map((group) => ({
        value: group.id,
        label: group.name
    })), [oemGroups])

    const onSubmit: SubmitHandler<typeof initialState> = async (data) => {
        await new Promise((res, rej) => {
            dispatch(fetchCreatePartnerDiscount({
                partner_id: data.partner_id,
                accrual_type: data.accrual_type,
                value: data.value,
                started_date: data.started_date.toISOString(),
                priority: data.priority,
                oem_groups: data.oem_groups,
                finished_date: data.finished_date?.toISOString()
            }))
                .unwrap()
                .then(() => {
                    notify.success('Скидка успешно создана')
                    reset()
                    onCreate?.()
                    res('')
                    handleClose()
                })
                .catch((e) => {
                    notify.error(getErrorMessage(e))
                    rej()
                })
        })
    }

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
        reset
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(createDiscountSchema)
    })

    return (
        <Modal
            isOpen={isOpen}
            title="Создание скидки"
            onRequestClose={handleClose}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <FormInput
                    label={"Юридческое лицо"}
                    id={"partner_id"}
                    options={partnerSelectOptions}
                    control={control}
                    error={errors['partner_id']}
                    type="select"
                    required={true}
                    placeholder="Выберите юрлицо"
                />
                <FormInput
                    label={"Тип начисления"}
                    id={"accrual_type"}
                    error={errors['accrual_type']}
                    options={accrualTypesSelectOptions}
                    control={control}
                    type="select"
                    required={true}
                    placeholder="Выберите тип начисления"
                />
                <FormInput
                    register={register}
                    label={"Значение"}
                    id={"value"}
                    type="number"
                    error={errors['value']}
                    required={true}
                    placeholder="Укажите значение скидки"
                />
                <FormInput
                    label={"Дата начала"}
                    id={"started_date"}
                    control={control}
                    type="datepicker"
                    error={errors['started_date']}
                    required={true}
                    placeholder="Выберите дату начала"
                />
                <FormInput
                    label={"Дата окончания (опционально)"}
                    id={"finished_date"}
                    control={control}
                    type="datepicker"
                    required={false}
                    error={errors['finished_date']}
                    placeholder="Выберите дату окончания"
                />
                <FormInput
                    register={register}
                    label={"Приоритет"}
                    id={"priority"}
                    min={0}
                    type="number"
                    error={errors['priority']}
                    required={true}
                    placeholder="Укажите приоритет выбора скидки"
                />
                <FormInput
                    label={"Категории товаров"}
                    id={"oem_groups"}
                    options={oemgroupsSelectOptions}
                    control={control}
                    error={errors['oem_groups']?.[0]}
                    isMulti
                    type="select"
                    required={true}
                    placeholder="Выберите категории товаров"
                />
                <div className="w-full flex gap-3">
                    <button onClick={handleClose} className="form_cancel_button">
                        Отменить
                    </button>
                    <button disabled={isSubmitting} className="form_handle_button" type="submit">
                        Добавить
                    </button>
                </div>
            </form>
        </Modal>
    )
}